import { hasStockType, type Product } from '../types/product.ts';

/** Milisegundos de `inStockSince`, o null si no hay (o no es una fecha válida). */
function arrivedAt(product: Product): number | null {
  if (!product.inStockSince) return null;
  const ms = Date.parse(product.inStockSince);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * «Lo más reciente» del catálogo: primero lo que LLEGÓ o VOLVIÓ a tener stock, por su
 * `inStockSince` (EBM lo anota: un artículo agotado desaparece de la web y, al reponerse,
 * vuelve como novedad). Lo que no trae fecha —ya tenía stock cuando EBM empezó a llevar la
 * cuenta, o el API todavía no la manda— va después, por productId (autoincremental: el id
 * más alto es el último artículo agregado).
 * Lo comparten la sección del inicio y la página /novedades para que nunca discrepen.
 */
export function sortNewestFirst(products: readonly Product[]): Product[] {
  return [...products].sort((a, b) => {
    const ta = arrivedAt(a);
    const tb = arrivedAt(b);
    if (ta !== null && tb !== null && ta !== tb) return tb - ta;
    if (ta !== null && tb === null) return -1;
    if (ta === null && tb !== null) return 1;
    return b.productId - a.productId;
  });
}

/**
 * Lo recién llegado es MERCANCÍA: los servicios («Instalación», «Mantenimiento»,
 * «Otros cargos») tienen su propia página y, como se crean en cualquier momento,
 * ocupaban la cabeza de la lista y sus filtros.
 */
export function newestMerchandise(products: readonly Product[]): Product[] {
  return sortNewestFirst(products.filter((p) => hasStockType(p.productType)));
}

export interface LatestCategory {
  name: string;
  count: number;
}

/** Nombre con el que se agrupa un artículo en los filtros; vacío = sin categoría. */
export function categoryOf(product: Product): string {
  return (product.productSubCategory ?? product.category ?? '').trim();
}

/**
 * Las categorías presentes, ordenadas por la novedad de su artículo más reciente:
 * la primera es la que recibió mercancía hace menos. Los artículos sin categoría
 * no generan filtro (siguen saliendo en «Todos»).
 */
export function latestCategories(products: readonly Product[]): LatestCategory[] {
  const byName = new Map<string, { count: number; newest: number }>();
  for (const p of products) {
    const name = categoryOf(p);
    if (!name) continue;
    const entry = byName.get(name);
    if (entry) {
      entry.count += 1;
      entry.newest = Math.max(entry.newest, p.productId);
    } else {
      byName.set(name, { count: 1, newest: p.productId });
    }
  }
  return [...byName.entries()]
    .sort((a, b) => b[1].newest - a[1].newest)
    .map(([name, { count }]) => ({ name, count }));
}

/** Los más recientes, opcionalmente de una sola categoría (null = todas). */
export function filterLatest(products: readonly Product[], category: string | null): Product[] {
  const sorted = sortNewestFirst(products);
  return category ? sorted.filter((p) => categoryOf(p) === category) : sorted;
}
